import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineClock,
  HiOutlinePencil,
  HiOutlineTrash,
} from 'react-icons/hi2';
import { authService } from '../../services';
import masterDataService from '../../services/masterDataService';
import SharedModal from '../../components/common/SharedModal';
import { toast } from 'react-toastify';
import PageLoading from '../../components/common/PageLoading';

const Shifts = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [formData, setFormData] = useState({
    shiftCode: '',
    startTime: '',
    endTime: '',
    breakMinutes: 0,
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadShifts();
    } else {
      setError('Please login to view shifts');
      setLoading(false);
    }
  }, [tenantId]);

  const loadShifts = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getShifts(tenantId);
      setShifts(data);
    } catch (err) {
      setError('Failed to load shifts. Please try again.');
      console.error('Error loading shifts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredShifts = shifts.filter((shift) =>
    shift.shiftCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (timeString) => {
    if (!timeString) return '';
    // Convert "HH:mm:ss" or "HH:mm" to "HH:mm" for input
    return timeString.substring(0, 5);
  };

  const handleAdd = () => {
    setEditingShift(null);
    setFormData({
      shiftCode: '',
      startTime: '',
      endTime: '',
      breakMinutes: 0,
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (shift) => {
    setEditingShift(shift);
    setFormData({
      shiftCode: shift.shiftCode || '',
      startTime: formatTime(shift.startTime),
      endTime: formatTime(shift.endTime),
      breakMinutes: shift.breakMinutes || 0,
      status: shift.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteShift(id);
      await loadShifts();
      setDeleteConfirm(null);
      toast.success('Shift deleted successfully!');
    } catch (err) {
      const msg = 'Failed to delete shift. Please try again.';
      setError(msg);
      toast.error(msg);
      console.error('Error deleting shift:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.shiftCode.trim()) {
      errors.shiftCode = 'Shift code is required';
    } else if (formData.shiftCode.length > 10) {
      errors.shiftCode = 'Shift code must be less than 10 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const shiftData = {
        tenantId: tenantId,
        shiftCode: formData.shiftCode.trim(),
        startTime: formData.startTime ? `${formData.startTime}:00` : null,
        endTime: formData.endTime ? `${formData.endTime}:00` : null,
        breakMinutes: parseInt(formData.breakMinutes) || 0,
        status: formData.status,
      };

      if (editingShift) {
        shiftData.shiftId = editingShift.shiftId;
        await masterDataService.updateShift(editingShift.shiftId, shiftData);
      } else {
        await masterDataService.createShift(shiftData);
      }

      await loadShifts();
      setShowModal(false);
      setFormData({ shiftCode: '', startTime: '', endTime: '', breakMinutes: 0, status: 'Active' });
      setEditingShift(null);
      toast.success(editingShift ? 'Shift updated successfully!' : 'Shift created successfully!');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save shift. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingShift(null);
    setFormData({ shiftCode: '', startTime: '', endTime: '', breakMinutes: 0, status: 'Active' });
    setFormErrors({});
  };

  if (loading) {
    return <PageLoading message="Loading shifts..." />;
  }

  return (
    <div className="shifts-page pagecontainer">
      <div className="page-header">
        <div className="header-content">
          <h2>Shifts</h2>
          <p>Manage your organization shifts</p>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search shifts..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <button className="btn-primary" onClick={handleAdd}>
            <HiOutlinePlus />
            Add Shift
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="table-info">
            {filteredShifts.length} {filteredShifts.length === 1 ? 'shift' : 'shifts'}
          </div>
        </div>

        {filteredShifts.length === 0 ? (
          <div className="empty-state">
            <HiOutlineClock className="empty-icon" />
            <h3>No shifts found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first shift'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Shift
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Shift Code</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Break (min)</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredShifts.map((shift) => (
                <tr key={shift.shiftId}>
                  <td>{shift.shiftId}</td>
                  <td className="name-cell">{shift.shiftCode}</td>
                  <td>{shift.startTime ? formatTime(shift.startTime) : '-'}</td>
                  <td>{shift.endTime ? formatTime(shift.endTime) : '-'}</td>
                  <td>{shift.breakMinutes || 0}</td>
                  <td>
                    <span className={`status-badge ${shift.status?.toLowerCase()}`}>
                      {shift.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    {shift.createdAt
                      ? new Date(shift.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(shift)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(shift)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingShift ? 'Edit Shift' : 'New Shift'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={handleCloseModal} disabled={submitting}>Cancel</button>
            <button type="submit" form="shift-form" className="btn-primary" disabled={submitting}>
              {submitting ? (editingShift ? 'Updating...' : 'Creating...') : (editingShift ? 'Update' : 'Create')}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="shift-form" onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="shiftCode">
                  Shift Code <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="shiftCode"
                  value={formData.shiftCode}
                  onChange={(e) =>
                    setFormData({ ...formData, shiftCode: e.target.value })
                  }
                  className={formErrors.shiftCode ? 'error' : ''}
                  placeholder="e.g., A, B, C, Morning, Evening"
                  maxLength={10}
                />
                {formErrors.shiftCode && (
                  <span className="field-error">{formErrors.shiftCode}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="startTime">Start Time</label>
                <input
                  type="time"
                  id="startTime"
                  value={formData.startTime}
                  onChange={(e) =>
                    setFormData({ ...formData, startTime: e.target.value })
                  }
                  placeholder="HH:mm"
                />
              </div>

              <div className="form-group">
                <label htmlFor="endTime">End Time</label>
                <input
                  type="time"
                  id="endTime"
                  value={formData.endTime}
                  onChange={(e) =>
                    setFormData({ ...formData, endTime: e.target.value })
                  }
                  placeholder="HH:mm"
                />
              </div>

              <div className="form-group">
                <label htmlFor="breakMinutes">Break Duration (minutes)</label>
                <input
                  type="number"
                  id="breakMinutes"
                  value={formData.breakMinutes}
                  onChange={(e) =>
                    setFormData({ ...formData, breakMinutes: e.target.value })
                  }
                  placeholder="0"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customWidth="400px"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button type="button" className="btn-danger" onClick={() => handleDelete(deleteConfirm.shiftId)}>Delete</button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>Are you sure you want to delete <strong>{deleteConfirm?.shiftCode}</strong>?</p>
          <p className="warning-text">This action cannot be undone. The shift will be marked as inactive.</p>
        </div>
      </SharedModal>
    </div>
  );
};

export default Shifts;

